import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, forkJoin, Observable, switchMap, throwError } from 'rxjs';
import { ToasterService } from '../../providers/toaster/toaster.service';
import { urlPattern } from '../../providers/regexPattern';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  private apiUrl = environment.apiUrl;
  authHeader: any
  constructor(private http: HttpClient, private authService: AuthService, private toasterService: ToasterService) {
    const authData = JSON.parse(localStorage.getItem('auth_data') || '{}');
    this.authHeader = new HttpHeaders({
      Authorization: `Bearer ${authData?.token}`,
    });

  }

  getAllCourses(
    page?: number | string,
    filters?: any[],
    courseType?: string
  ): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let params = new HttpParams();

        if (page) {
          params = params.set('page', page.toString());
        }

        // If API supports course_type query param (video/flashcard/etc), pass it.
        if (courseType && courseType !== 'all') {
          params = params.set('course_type', courseType);
        }

        if (filters && filters.length > 0) {
          filters.forEach((filter, index) => {
            params = params.set(`filter[${index}][filter_key]`, filter.filter_key);
            params = params.set(`filter[${index}][filter_value]`, filter.filter_value);
          });
        }

        return this.http.get(`${this.apiUrl}master/course/get-all`, {
          headers: headers,
          params: params,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }


  getPublishedCourses(page?: number | string): Observable<any> {
    return this.getAllCourses(page, [
      { filter_key: 'is_published', filter_value: 1 },
    ]);
  }


  createCourse(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/create`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  updateCourseContent(id: any, data: any): Observable<any> {
    
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
         let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/update/${id}`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  getCourseContent(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/course/get-course/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  // ******** structure api ************
  getStructure(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/course/structure/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addNode(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/create`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  updateNode(data: any, parent_id: number | string): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/update/${parent_id}`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteNode(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/course/structure/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addVideoNode(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/create/video`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  updateVideoNode(data: any, id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/update/video/${id}`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteVideoNode(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/delete/video/${id}`, {}, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addCertificateNode(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/create/certificate`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  updateCertificateNode(data: any, id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/update/certificate/${id}`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteCertificateNode(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/delete/certificate/${id}`, {}, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addBadgeNode(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/create/badge`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  updateBadgeNode(data: any, id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/course/structure/update/badge/${id}`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteBadgeNode(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/delete/badge/${id}`, {}, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  saveFlashCardStructure(data: any): Observable<any> {
        return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/structure/create/flash`,data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  // ******** structure api ************
  // ******** Deatils api ************

  getCourseDeatils(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/course/price/detail/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  getChaptersData(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/course/structure/chapter-name/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addCourseDeatils(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.post(`${this.apiUrl}master/course/others/detail/create`, data, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  // ******** Deatils api ************
  // ******** others api ************


  getOthersData(id: any) {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return forkJoin({
          testimonials: this.http.get(`${this.apiUrl}master/testimonial/get-all`, {
            headers,
            observe: 'response'
          }),
          comments: this.http.get(`${this.apiUrl}master/comment/get-all`, {
            headers,
            observe: 'response'
          }),
          faculty: this.http.get(`${this.apiUrl}master/users/faculty`, {
            headers,
            observe: 'response'
          }),
          users: this.http.get(`${this.apiUrl}master/users/get-all`, {
            headers,
            observe: 'response'
          }),
          formdata: this.http.get(`${this.apiUrl}master/course/others/${id}`, {
            headers,
            observe: 'response'
          }),
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addOthersData(data: any, flag: any = 0, id: any | null): Observable<any> {
    let url = ''
    if (flag == 1) {
      url = `${this.apiUrl}master/course/others/store`
    } else if (flag == 0 && id != null) {
      url = `${this.apiUrl}master/course/others/update/${id}`
    }

    console.log(url, flag);


    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(url, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }



  addTestimonial(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/testimonial/create`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addComments(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/comment/create`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteTestimonial(id: number | string): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/testimonial/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  deleteComment(id: number | string): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/comment/${id}/delete`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }


  // ******** others api ************
  // ******** reviews Api ************

  getReviews(id: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/review/course/${id}`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  addReviews(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
         let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/review/create`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  setIsPublished(data: any): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
         let reqHeaders = headers;
        // If sending FormData (file upload), remove any explicit Content-Type so
        // the browser can set the proper multipart/form-data boundary.
        if (data instanceof FormData) {
          reqHeaders = reqHeaders.delete('Content-Type');
        }
        return this.http.post(`${this.apiUrl}master/review/update/status`, data, {
          headers: reqHeaders,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  // ******** course status update apis ************
   // These methods handle course status transitions: draft -> published
  // Each method:
  //   1. Gets auth headers from AuthService (ensures valid token)
  //   2. Creates JSON body with is_draft and is_published
  //   3. Sets Content-Type to application/json
  //   4. PUTs to master/course/update/status/{courseId} endpoint
  //   5. Catches 401 errors and triggers re-authentication
  
  /**
   * Change course status to DRAFT
   * Called when user clicks "Draft" in course card dropdown
   * @param courseId - The course ID to update
   * @returns Observable with server response
   */
  setCourseAsDraft(courseId: number | string, extra?: { course_type?: any; course_name?: any; description?: any }): Observable<any> {  
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const body = { is_draft: 1, is_published: 0 };
        return this.http.post(`${this.apiUrl}master/course/update/status/${courseId}`, body, {
          headers: headers.set('Content-Type', 'application/json'),
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  setCourseAsPublished(courseId: number | string, extra?: { course_type?: any; course_name?: any; description?: any }): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        const body = { is_draft: 0, is_published: 1 };
        return this.http.post(`${this.apiUrl}master/course/update/status/${courseId}`, body, {
          headers: headers.set('Content-Type', 'application/json'),
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  

  // ******** course status update apis ************
  // ******** reviews Api ************







  getUsers(): Observable<any> {
    return this.authService.getHeader().pipe(
      switchMap((headers: HttpHeaders) => {
        return this.http.get(`${this.apiUrl}master/users/get-all`, {
          headers: headers,
          observe: 'response',
        });
      }),
      catchError((error) => {
        if (error.status === 401) {
          console.error('Unauthorized access:', error);
          this.handelTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }





  handelTokenExpired() {
    this.toasterService.setToaster('default', 'Session Expired', 'The session has expired. Please log in again.');
    this.authService.logout();
  }

  
}
